import { useState, useCallback, useEffect } from 'react';
import { Chat, Message, RecipientType } from '../types';
import { chatService } from '../services/chatService';
import { logger } from '../../../shared/utils/logger';
import { globalWebSocket } from '../../../shared/ws/globalWebSocket';
import { useOrdersStore } from '../../../shared/store/ordersStore';

// Расширяем интерфейс Message для WebSocket данных
interface WebSocketMessage extends Message {
  unreadCount?: number;
}

/**
 * Простой оптимизированный хук для чатов
 * Без React Query - использует вашу существующую архитектуру
 */
export const useChatMessagesSimple = () => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [activeChat, setActiveChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // 🔄 Локальное хранилище для WebSocket сообщений (не пропадают при перезагрузке чата)
  const [localWebSocketMessages, setLocalWebSocketMessages] = useState<Map<string, Message[]>>(new Map());
  
  // Состояние для пагинации сообщений
  const [currentPage, setCurrentPage] = useState(1);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // 🔥 СЧЕТЧИКИ ТЕПЕРЬ УПРАВЛЯЮТСЯ ТОЛЬКО В ordersStore
  // Никаких обновлений глобального счетчика отсюда!

  /**
   * Загрузка списка чатов (из БД - быстро!)
   */
  const loadChats = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Сбрасываем состояния пагинации при загрузке чатов
      setMessagesHasMore(false);
      setIsLoadingMore(false);
      
      // Ваш существующий API - данные из БД
      const result = await chatService.loadChats(1, 500);
      
      setChats(result.chats);
      
      // 🔥 ИСПРАВЛЕНИЕ: Обновляем глобальный счетчик при загрузке чатов
      const totalUnreadCount = result.chats.reduce((total: number, chat: any) => {
        return total + (chat.unreadCount || 0);
      }, 0);
      
      const { updateUnreadMessagesCount } = useOrdersStore.getState();
      updateUnreadMessagesCount(totalUnreadCount);
      
      logger.debug(`✅ Загружено ${result.chats.length} чатов из БД, общий счетчик: ${totalUnreadCount}`);
    } catch (error) {
      logger.error('❌ Ошибка загрузки чатов:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Выбор активного чата
   */
  const selectChat = useCallback(async (chatId: string) => {
    console.log(`🔍 selectChat вызван для chatId: ${chatId}`);
    console.log(`📋 Доступные чаты:`, chats.map(c => ({ id: c.id, title: c.title })));
    
    const chat = chats.find(c => c.id === chatId);
    if (!chat) {
      console.error(`❌ Чат ${chatId} не найден в списке из ${chats.length} чатов`);
      logger.error(`❌ Чат ${chatId} не найден`);
      return;
    }

    console.log(`✅ Найден чат:`, chat);
    setActiveChat(chat);

    // Всегда загружаем первую страницу с сервера

    // Загружаем первую страницу сообщений (последние 30)
    try {
      console.log(`🔄 Загружаем сообщения для чата ${chatId}...`);
      setIsLoading(true);
      const result = await chatService.loadChatMessages(chatId, 1, 30);
      console.log(`✅ Загружено ${result.messages.length} сообщений, hasMore: ${result.hasMore}`);
      
      setMessages(result.messages);
      
      // Сохраняем информацию о пагинации
      setMessagesHasMore(result.hasMore);
      setMessagesTotal(result.total || 0);
      setCurrentPage(1);
      
      logger.debug(`✅ Загружено ${result.messages.length} сообщений для чата ${chatId} (всего: ${result.total}, есть еще: ${result.hasMore})`);
    } catch (error) {
      console.error(`❌ Ошибка загрузки сообщений чата ${chatId}:`, error);
      logger.error(`❌ Ошибка загрузки сообщений чата ${chatId}:`, error);
      // При ошибке сбрасываем состояние
      setMessages([]);
      setMessagesHasMore(false);
      setActiveChat(null);
    } finally {
      console.log(`🔄 Завершаем загрузку для чата ${chatId}, setIsLoading(false)`);
      setIsLoading(false);
    }
  }, [chats]);

  /**
   * Загрузка дополнительных сообщений (пагинация)
   */
  const loadMoreMessages = useCallback(async () => {
    if (!activeChat || !messagesHasMore || isLoadingMore) {
      return;
    }

    try {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      
      logger.debug(`📄 Загружаем страницу ${nextPage} для чата ${activeChat.id}`);
      
      const result = await chatService.loadChatMessages(activeChat.id, nextPage, 30);
      
      // Сохраняем текущую позицию скролла перед добавлением сообщений
      const container = document.querySelector('.chat-widget-messages');
      const oldScrollHeight = container?.scrollHeight || 0;
      const oldScrollTop = container?.scrollTop || 0;
      
      // Добавляем новые сообщения к существующим (в начало, так как это более старые)
      setMessages(prev => {
        const newMessages = [...result.messages, ...prev];
        
        // Восстанавливаем позицию скролла после обновления DOM
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const heightDifference = newScrollHeight - oldScrollHeight;
            // Устанавливаем новую позицию с учетом добавленной высоты
            container.scrollTop = oldScrollTop + heightDifference;
          }
        }, 10);
        
        return newMessages;
      });
      
      // Обновляем состояние пагинации
      setCurrentPage(nextPage);
      setMessagesHasMore(result.hasMore);
      
      logger.debug(`✅ Загружено еще ${result.messages.length} сообщений (страница ${nextPage})`);
      
    } catch (error) {
      logger.error(`❌ Ошибка загрузки дополнительных сообщений:`, error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [activeChat, messagesHasMore, isLoadingMore, currentPage]);

  /**
   * Отправка сообщения с оптимистичным обновлением
   */
  const sendMessage = useCallback(async (chatId: string, content: string, recipient: RecipientType): Promise<Message> => {
    if (!activeChat) throw new Error('Нет активного чата');

    // Создаем оптимистичное сообщение
    const optimisticMessage: Message = {
      id: `temp_${Date.now()}`,
      content,
      author: 'Вы',
      is_own: true,
      created_at: new Date().toISOString(),
      status: 'sending',
      recipient,
      files: [],
      message_type: 'text'
    };

    // Мгновенно показываем в UI
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      // Отправляем на сервер
      const serverMessage = await chatService.sendMessage(chatId, content, recipient);
      
      // Заменяем оптимистичное сообщение на серверное
      setMessages(prev => prev.map(msg => 
        msg.id === optimisticMessage.id ? serverMessage : msg
      ));

      // Обновляем последнее сообщение в чате
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, lastMessage: content, lastMessageTime: serverMessage.created_at }
          : chat
      ));

      logger.debug('✅ Сообщение отправлено успешно');
      return serverMessage;
      
    } catch (error) {
      // Убираем оптимистичное сообщение при ошибке
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      
      logger.error('❌ Ошибка отправки сообщения:', error);
      throw error;
    }
  }, [activeChat]);

  /**
   * Отправка файлов
   */
  const sendFileMessage = useCallback(async (chatId: string, files: File[], recipient: RecipientType, messageText: string): Promise<Message> => {
    // Аналогично sendMessage, но для файлов
    const message = await chatService.sendFileMessage(chatId, files, recipient, messageText);
    
    // Добавляем в локальный стейт
    setMessages(prev => [...prev, message]);
    
    return message;
  }, []);

  /**
   * Поиск чата заказа в уже загруженном списке
   */
  const findOrderChat = useCallback(async (orderId: string): Promise<Chat | null> => {
    // Ищем в уже загруженном списке чатов
    let chat = chats.find(c => c.order_id === orderId);
    
    if (chat) {
      logger.debug(`✅ Найден чат для заказа ${orderId} в загруженном списке:`, chat.title);
      return chat;
    } else {
      logger.warn(`⚠️ Чат для заказа ${orderId} не найден в загруженном списке. Пытаемся обновить список чатов...`);
      
      // 🔥 ПОПЫТКА ОБНОВИТЬ СПИСОК ЧАТОВ
      // Возможно чат был только что создан и еще не попал в локальный список
      try {
        await loadChats();
        
        // Ищем снова в обновленном списке
        chat = chats.find(c => c.order_id === orderId);
        
        if (chat) {
          logger.debug(`✅ Чат для заказа ${orderId} найден после обновления списка:`, chat.title);
          return chat;
        } else {
          logger.warn(`⚠️ Чат для заказа ${orderId} все еще не найден. Возможно, заказ еще не был отмечен как просмотренный.`);
          return null;
        }
      } catch (error) {
        logger.error(`❌ Ошибка обновления списка чатов при поиске чата заказа ${orderId}:`, error);
        return null;
      }
    }
  }, [chats, loadChats]);

  /**
   * Добавление нового сообщения (от WebSocket)
   */
  const addMessage = useCallback((message: Message, chatId: string) => {
    // Добавляем в активный чат если он соответствует
    // Проверяем как по ID чата, так и по order_id (для случаев chat_1134)
    let isActiveChat = activeChat?.id === chatId;
    if (!isActiveChat && chatId.startsWith('chat_') && activeChat?.order_id) {
      const orderId = chatId.replace('chat_', '');
      isActiveChat = String(activeChat.order_id) === String(orderId);
    }
    
    if (isActiveChat) {
      setMessages(prev => [...prev, message]);
    } else {
      // На странице списка заказов НЕ активируем чат автоматически
      // Сообщение просто обновит список чатов и счетчики
    }
    
    // Обновляем только локальный стейт - кеш не используется
    
    // Обновляем чат в списке и пересортировываем
    setChats(prev => {
      const updatedChats = prev.map(chat => {
        if (chat.id === chatId) {
          // 🔥 ИСПРАВЛЕНИЕ: Правильно обновляем счетчик непрочитанных
          let newUnreadCount = chat.unreadCount;
          
          if (!message.is_own) {
            // Если сообщение НЕ наше, используем счетчик из WebSocket или увеличиваем локальный
            if (typeof message.unreadCount === 'number') {
              // Backend передал актуальный счетчик - используем его
              newUnreadCount = message.unreadCount;
            } else {
              // Fallback: увеличиваем локальный счетчик
              newUnreadCount = (chat.unreadCount || 0) + 1;
            }
          }
          // Если сообщение наше - счетчик не меняется
          
          return {
            ...chat,
            lastMessage: message.content,
            lastMessageTime: message.created_at,
            sortTime: message.created_at, // Обновляем sortTime для правильной сортировки
            unreadCount: newUnreadCount
          };
        }
        return chat;
      });
      
      // 🔥 ИСПРАВЛЕНИЕ: Обновляем глобальный счетчик при получении нового сообщения
      if (!message.is_own) {
        // Пересчитываем общий счетчик из всех чатов
        const totalUnreadCount = updatedChats.reduce((total, chat) => {
          return total + (chat.unreadCount || 0);
        }, 0);
        
        // Обновляем глобальный счетчик
        const { updateUnreadMessagesCount } = useOrdersStore.getState();
        updateUnreadMessagesCount(totalUnreadCount);
        
        logger.debug(`🔄 Обновлен общий счетчик после нового сообщения: ${totalUnreadCount}`);
      }
      
      // Пересортировываем чаты после обновления
      return chatService.sortChatsWithManagerFirst(updatedChats);
    });

    logger.debug('📨 Новое сообщение добавлено');
  }, [activeChat]);

  /**
   * Сброс активного чата
   */
  const clearActiveChat = useCallback(() => {
    setActiveChat(null);
    setMessages([]);
    logger.debug('🧹 Активный чат сброшен');
  }, []);

  /**
   * Отметка как прочитанное
   */
  const markAsRead = useCallback(async (chatId: string) => {
    try {
      await chatService.markChatAsRead(chatId);
      
      // Находим количество непрочитанных сообщений в этом чате
      const currentChat = chats.find(chat => chat.id === chatId);
      const unreadCountToSubtract = currentChat?.unreadCount || 0;
      
      // Обновляем локальный стейт
      setChats(prev => prev.map(chat => {
        if (chat.id === chatId) {
          return { ...chat, unreadCount: 0 };
        }
        return chat;
      }));
      
      // 🔥 ИСПРАВЛЕНИЕ: Пересчитываем ОБЩИЙ счетчик из всех чатов после обновления
      if (unreadCountToSubtract > 0) {
        // Вычисляем новый общий счетчик из обновленного списка чатов
        setChats(prev => {
          const updatedChats = prev.map(chat => {
            if (chat.id === chatId) {
              return { ...chat, unreadCount: 0 };
            }
            return chat;
          });
          
          // Пересчитываем общий счетчик из всех чатов
          const totalUnreadCount = updatedChats.reduce((total, chat) => {
            return total + (chat.unreadCount || 0);
          }, 0);
          
          // Обновляем глобальный счетчик
          const { updateUnreadMessagesCount } = useOrdersStore.getState();
          updateUnreadMessagesCount(totalUnreadCount);
          
          logger.debug(`🔄 Пересчитан общий счетчик: ${totalUnreadCount} (было вычтено ${unreadCountToSubtract} из чата ${chatId})`);
          
          return updatedChats;
        });
      }
      
      logger.debug(`✅ Чат ${chatId} отмечен как прочитанный`);
    } catch (error) {
      logger.error(`❌ Ошибка отметки чата ${chatId} как прочитанного:`, error);
    }
  }, [chats]);

  /**
   * Обработка нового сообщения из WebSocket (УСТАРЕЛО - теперь не используется)
   */
  const handleWebSocketMessage = useCallback((data: any) => {
    
    if (data.type === 'new_message' && data.message && data.chat_id) {
      // Преобразуем данные вебхука в формат WebSocketMessage
      const message: WebSocketMessage = {
        id: data.message.id || `ws_${Date.now()}`,
        content: data.message.content || '',
        author: data.message.author || 'Неизвестно',
        is_own: data.message.is_own || false,
        created_at: data.message.created_at || new Date().toISOString(),
        status: data.message.status || 'sent',
        recipient: data.message.recipient,
        files: data.message.files || [],
        message_type: data.message.message_type || 'text',
        // ✅ ДОБАВЛЯЕМ АКТУАЛЬНЫЙ СЧЕТЧИК ИЗ WEBSOCKET
        unreadCount: data.unread_count,
        // 🎯 КОПИРУЕМ ВСЕ СИСТЕМНЫЕ ФЛАГИ из WebSocket
        is_system: data.message.is_system || false,
        is_rejection: data.message.is_rejection || false,
        is_calc_cancelled: data.message.is_calc_cancelled || false,
        is_completion_notification: data.message.is_completion_notification || false,
        is_defect_notification: data.message.is_defect_notification || false,
        is_contractor_price: data.message.is_contractor_price || false,
        is_contractor_accepted: data.message.is_contractor_accepted || false,
        is_status_change: data.message.is_status_change || false,
        is_deadline_notification: data.message.is_deadline_notification || false,
        is_order_defect: data.message.is_order_defect || false,
        is_manager_rejection: data.message.is_manager_rejection || false,
        is_revision_request: data.message.is_revision_request || false
      };

      // Определяем chatId из данных
      let chatId = data.chat_id;
      
      // Если chat_id в формате 'chat_ORDER_ID', преобразуем в нужный формат
      if (chatId.startsWith('chat_')) {
        const orderId = chatId.replace('chat_', '');
        // Ищем чат по order_id (сравниваем как строки)
        const chat = chats.find(c => String(c.order_id) === String(orderId));
        if (chat) {
          chatId = chat.id;
        } else {
          // Если чат не найден, возможно нужно обновить список чатов
          loadChats();
          return;
        }
      }

      addMessage(message, chatId);
    }
  }, [chats, addMessage, loadChats]);

  // Загружаем чаты при первом рендере
  useEffect(() => {
    loadChats();
  }, []); // Пустой массив зависимостей - выполняется только один раз



  // 🔗 Прямая подписка на серверный WebSocket для мгновенного появления сообщений в открытом чате
  // Не затрагивает счетчики; только добавляет сообщение в локальный список при активном чате
  useEffect(() => {
    const service = globalWebSocket.getService();
    const handler = (data: any) => handleWebSocketMessage(data);
    service.on('new_message', handler);
    return () => {
      service.off('new_message', handler);
    };
  }, [handleWebSocketMessage]);


  // 🔄 ЛОКАЛЬНЫЕ СЧЕТЧИКИ ТЕПЕРЬ НЕ НУЖНЫ
  // Все управление счетчиками происходит в ordersStore через realtimeSync.ts

  return {
    // Данные
    chats,
    activeChat,
    messages,
    isLoading,
    
    // Пагинация сообщений
    messagesHasMore,
    messagesTotal,
    isLoadingMore,
    currentPage,
    
    // Методы
    loadChats,
    selectChat,
    sendMessage,
    sendFileMessage,
    findOrderChat,
    clearActiveChat,
    markAsRead,
    addMessage,
    loadMoreMessages
  };
};
